import { CloseIcon, nodataIcon } from '@/shared/ui/assets';
import {
  TextInput,
  AutoCompleteInput,
  CustomButton,
  CustomSelect,
  CustomTabs,
} from '@/shared/ui/components';
import { LoadingOutlined } from '@ant-design/icons';
import { Drawer, Spin } from 'antd';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { GOOGLE_API_KEY } from '@/shared/config';
import { idArr } from '../lib/createReferringPhysicianDrawer';
import { useCreateReferringPhysicianDrawer } from './useCreateReferringPhysicianDrawer';
import '@/shared/ui/components/drawers/style.scss';
import ExclamationCircleIcon from './assets/images/exclamation-circle-icon.svg';
import SearchIcon from './assets/images/search-icon.svg';
import type { CreateReferringPhysicianDrawerProps } from './createReferringPhysician.types';
import './createReferringPhysicianDrawer.scss';

const CreateReferringPhysicianDrawer = ({
  handleClickClose,
  data,
  handleClickCreate,
  countries,
  isCreatingReferringPhysician,
  visible,
}: CreateReferringPhysicianDrawerProps) => {
  const {
    state,
    npiInputRef,
    isWebView,
    shouldFormatPhoneNumber,
    handleKeyDownInput,
    handleScroll,
    onClickClose,
    onChange,
    onChangeSelection,
    onClickCreate,
    onChangeInputAutoComplete,
    onChangeAutoComplete,
    clearAutoComplete,
    handleNpiChange,
    handleSearchClick,
    navigateTab,
  } = useCreateReferringPhysicianDrawer({
    handleClickClose,
    data,
    handleClickCreate,
    countries,
    visible,
  });

  /**
   * Renders the referring-physician form shared by the NPI-search and manual-entry tabs of the create drawer. Lays out the information block (first name, last name, title, plus NPI only on the manual tab) and the contact block (country and address/city autocompletes, a state dropdown where phone formatting applies and a plain input otherwise, zip, masked phone, after-hours phone, email and fax), each bound to state.referringPhysicianInfo with its error, above the submit button.
   *
   * @returns The scrollable drawer body markup.
   */
  const formRenderer = () => (
    <div className="drawer-body-container" onScroll={handleScroll}>
      <div className="drawer-body">
        <div className="drawer-header">
          <span>{t`Physician Information`}</span>
        </div>
        <div className="drawer-content">
          <TextInput
            name="firstName"
            id={idArr[0]}
            onKeyDown={handleKeyDownInput}
            label={t`First name`}
            placeholder={t`Enter physician’s first name`}
            value={state.referringPhysicianInfo.firstName}
            onChange={onChange}
            isError={!!state.errorObject.firstName}
            errorMessage={state.errorObject.firstName}
          />
          <TextInput
            name="lastName"
            id={idArr[1]}
            onKeyDown={handleKeyDownInput}
            label={t`Last name`}
            placeholder={t`Enter physician’s last name`}
            value={state.referringPhysicianInfo.lastName}
            onChange={onChange}
            isError={!!state.errorObject.lastName}
            errorMessage={state.errorObject.lastName}
          />
          <TextInput
            name="title"
            id={idArr[2]}
            onKeyDown={handleKeyDownInput}
            label={t`Title`}
            placeholder={t`Enter physician’s title`}
            value={state.referringPhysicianInfo.title}
            onChange={onChange}
            isError={!!state.errorObject.title}
            errorMessage={state.errorObject.title}
          />
          {state.activeTab === '2' && (
            <TextInput
              name="npi"
              id={idArr[3]}
              onKeyDown={handleKeyDownInput}
              label={
                <div>
                  {t`NPI number`} <span className="optional-label">{`(${t`optional`})`}</span>
                </div>
              }
              placeholder={t`Enter physician's NPI number`}
              isNumberFormat
              value={state.referringPhysicianInfo.npi}
              onChange={onChange}
              isError={!!state.errorObject.npi}
              errorMessage={state.errorObject.npi}
            />
          )}
        </div>
      </div>
      <div className="drawer-body">
        <div className="drawer-header">
          <span>{t`Physician Contact`}</span>
        </div>
        <div className="drawer-content">
          <CustomSelect
            title={t`Country`}
            aria-label={t`Country`}
            showSearch
            allowClear={false}
            isError={!!state.errorObject.country}
            errorMessage={state.errorObject.country}
            name="country"
            id={idArr[3]}
            size="middle"
            options={countries}
            value={state.country}
            onChange={onChangeSelection}
          />
          <AutoCompleteInput
            googleApiKey={GOOGLE_API_KEY}
            isSearchCity={false}
            allowClear
            size="small"
            id={idArr[4]}
            title={t`Address`}
            placeholder={t`Enter physician's address`}
            country={state.referringPhysicianInfo.country}
            value={state.referringPhysicianInfo.address}
            onChangeInput={onChangeInputAutoComplete}
            onChangeAddress={onChangeAutoComplete}
            isError={!!state.errorObject.address}
            errorMessage={state.errorObject.address}
            eventClear={clearAutoComplete}
          />
          <div className="inline-input">
            <AutoCompleteInput
              googleApiKey={GOOGLE_API_KEY}
              className="city-input"
              size="small"
              isSearchCity
              id={idArr[5]}
              title={t`City`}
              placeholder={t`Enter physician's city`}
              country={state.referringPhysicianInfo.country}
              value={state.referringPhysicianInfo.city}
              onChangeInput={onChangeInputAutoComplete}
              onChangeAddress={onChangeAutoComplete}
              isError={!!state.errorObject.city}
              errorMessage={state.errorObject.city}
              eventClear={clearAutoComplete}
            />
            {shouldFormatPhoneNumber ? (
              <CustomSelect
                className="state-input"
                size="middle"
                name="state"
                id={idArr[6]}
                title={t`State`}
                aria-label={t`State`}
                options={state.divisions}
                placeholder={t`Select a state`}
                value={state.state}
                onChange={onChangeSelection}
                isError={!!state.errorObject.state}
                errorMessage={state.errorObject.state}
                showSearch
                allowClear={false}
              />
            ) : (
              <TextInput
                className="state-input"
                name="state"
                id={idArr[6]}
                onKeyDown={handleKeyDownInput}
                label={t`State`}
                placeholder={t`Enter state`}
                value={state.referringPhysicianInfo.state}
                onChange={onChange}
                isError={!!state.errorObject.state}
                errorMessage={state.errorObject.state}
              />
            )}
          </div>
          <TextInput
            name="zip"
            id={idArr[7]}
            onKeyDown={handleKeyDownInput}
            label={t`Zip code`}
            placeholder={t`Enter zip code`}
            value={state.referringPhysicianInfo.zip}
            onChange={onChange}
            isError={!!state.errorObject.zip}
            errorMessage={state.errorObject.zip}
          />
          <TextInput
            isPhoneNumberMask
            name="phone1"
            id={idArr[8]}
            onKeyDown={handleKeyDownInput}
            label={t`Phone number`}
            placeholder={shouldFormatPhoneNumber ? '800-555-1234' : 'Enter primary phone number'}
            value={state.referringPhysicianInfo.phone1}
            onChange={onChange}
            isError={!!state.errorObject.phone1}
            errorMessage={state.errorObject.phone1}
          />
          <TextInput
            isPhoneNumberMask
            name="nightPhone"
            id={idArr[9]}
            onKeyDown={handleKeyDownInput}
            label={
              <div>
                {t`After hours phone number`}{' '}
                <span className="optional-label">{`(${t`optional`})`}</span>
              </div>
            }
            placeholder={shouldFormatPhoneNumber ? '800-555-1234' : 'Enter hours phone number'}
            value={state.referringPhysicianInfo.nightPhone}
            onChange={onChange}
            isError={!!state.errorObject.nightPhone}
            errorMessage={state.errorObject.nightPhone}
          />
          <TextInput
            name="email"
            id={idArr[10]}
            onKeyDown={handleKeyDownInput}
            label={
              <div>
                {t`Email`} <span className="optional-label">{`(${t`optional`})`}</span>
              </div>
            }
            placeholder={t`Enter physician's email`}
            value={state.referringPhysicianInfo.email}
            onChange={onChange}
            isError={!!state.errorObject.email}
            errorMessage={state.errorObject.email}
          />
          <TextInput
            isPhoneNumberMask
            name="fax"
            id={idArr[11]}
            onKeyDown={handleKeyDownInput}
            label={
              <div>
                {t`Fax number`} <span className="optional-label">{`(${t`optional`})`}</span>
              </div>
            }
            placeholder={shouldFormatPhoneNumber ? '800-555-1234' : 'Enter primary phone number'}
            value={state.referringPhysicianInfo.fax}
            onChange={onChange}
            isError={!!state.errorObject.fax}
            errorMessage={state.errorObject.fax}
          />
        </div>
      </div>
      <div className="bottom-button-container">
        <CustomButton
          size="middle"
          id="create-referring-physician-button"
          onClick={onClickCreate}
          loading={isCreatingReferringPhysician}
        >
          {t`Add New Physician`}
        </CustomButton>
      </div>
    </div>
  );

  /**
   * Builds the two tabs of the create-referring-physician drawer. The Search by NPI tab pairs a ten-digit NPI input and Search button with one of four bodies — a searching spinner, a not-found empty state, the physician form once a match is returned, or a prompt to enter a number — while the Manual Entry tab renders that same form directly.
   *
   * @returns Tab descriptors for the drawer.
   */
  const getTabItems = () => [
    {
      key: '1',
      label: <div>{t`Search by NPI`}</div>,
      children: (
        <div className="search-by-npi-tab">
          <div
            className={classNames('search-container', {
              'is-scrolling': state.isScrolling,
            })}
          >
            <div className="npi-number-input-container">
              <TextInput
                inputRef={npiInputRef}
                name="npi"
                placeholder={t`Enter 10-digit NPI number`}
                type="number"
                value={state.npi}
                onChange={handleNpiChange}
                isError={!!state.errorObject.npi}
              />
              {!!state.errorObject.npi && (
                <div className="error-container">
                  <ReactSVG src={ExclamationCircleIcon} />
                  <span className="error-text">{state.errorObject.npi}</span>
                </div>
              )}
            </div>
            <CustomButton
              size="small"
              id="search-referring-physician-button"
              icon={<ReactSVG src={SearchIcon} />}
              onClick={handleSearchClick}
              disabled={state.isSearchingPhysician}
            >
              {t`Search`}
            </CustomButton>
          </div>
          {state.isSearchingPhysician ? (
            <div className="searching-container">
              <Spin indicator={<LoadingOutlined spin style={{ fontSize: 20 }} />} />
              <div>{t`Searching for physician...`}</div>
            </div>
          ) : state.notFoundPhysician ? (
            <div className="not-found-container">
              <ReactSVG src={nodataIcon} />
              <span className="message">
                {t`No physician was found for the NPI number you entered. Please check the number and try again.`}
              </span>
            </div>
          ) : state.searchedReferringPhysicianInfo ? (
            formRenderer()
          ) : (
            <div className="message-container">
              <span className="message">
                {t`Enter the 10-digit NPI number to search for a physician`}
              </span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: '2',
      label: <div>{t`Manual Entry`}</div>,
      children: formRenderer(),
    },
  ];

  const RenderCustomTabs = CustomTabs as any;

  return (
    <Drawer
      id="create-referring-physician-drawer"
      aria-label={t`Add new physician`}
      rootClassName={classNames(
        'custom-drawer',
        'referring-physician-drawer',
        isWebView && 'create-referring-drawer-modal-web-view',
      )}
      width={500}
      closable={false}
      maskClosable={false}
      onClose={onClickClose}
      open={visible}
      destroyOnHidden
    >
      <div className="drawer-title-container">
        <span className="drawer-title">{t`Add new physician `}</span>
        <img src={CloseIcon} className="close-btn" alt="Close icon" onClick={onClickClose} />
      </div>
      <div className="no-search-physician-container">
        <RenderCustomTabs
          className="add-new-physician-tabs"
          tabTitleClassName="tab-title"
          items={getTabItems() as any}
          activeKey={state.activeTab}
          onChange={navigateTab}
        />
      </div>
    </Drawer>
  );
};

export default CreateReferringPhysicianDrawer;
